<template>
  <div class="kk-designer__activity-menu_container" :style="{ ...position }">
    <div class="kk-designer__activity-menu" :style="{ transform }">
      <div v-if="showOptions.length" class="kk-designer__activity-menu-wrapper" :class="mode">
        <button
          v-for="(item, index) in showOptions"
          :key="index"
          :title="item.title"
          :class="{ 'kk-designer__activity-menu-disabled': item.disabled }"
          :data-test-id="item.testId"
          @click="item.func()"
        >
          <span class="kk-designer__activity-menu-icon" :class="item.icon"></span>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import hotkeys from "hotkeys-js";
import { HotkeyScopes } from "../../../../../utils/constants";
import { ColumnTypes, RowTypes } from "../../../cell/CellConstants";
import { icons, ProcessTypes } from "../../../../../utils/icons";
import DesignerMixin from "../../../DesignerMixin";
export default {
  mixins: [DesignerMixin],
  data() {
    return {
      calculatedPosition: {},
      transform: "unset",
    };
  },
  computed: {
    data() {
      return [
        {
          icon: icons.AddRow,
          title: this.$t("contextMenu.activity.addRowGro"),
          func: () => this.addRow(RowTypes.RowGroup),
          hide: !(this.row.type === RowTypes.RowGroup),
          testId: "add-row-group",
        },
        {
          icon: icons.AddRow,
          title: this.$t("contextMenu.activity.addRow"),
          func: () => this.addRow(RowTypes.Grid),
          hide: !(this.row.type === RowTypes.Grid),
          testId: "add-grid-row",
        },
        {
          icon: icons.AddColumn,
          title: this.$t("contextMenu.activity.addColGro"),
          func: () => this.addColumn(ColumnTypes.ColumnGroup),
          hide: !(this.column.type === ColumnTypes.ColumnGroup),
          testId: "add-column-group",
        },
        {
          icon: icons.AddColumn,
          title: this.$t("contextMenu.activity.addColumn"),
          func: () => this.addColumn(ColumnTypes.Grid),
          hide: !(this.column.type === ColumnTypes.Grid),
          testId: "add-grid-column",
        },
        {
          icon: icons.AddRow,
          title: this.$t("contextMenu.activity.addRow"),
          func: this.addActivity,
          hide: !(this.row.type === RowTypes.Activity),
          testId: "add-row",
        },
        {
          icon: icons.RemoveRow,
          title: this.$t("contextMenu.activity.removeRow"),
          func: this.removeRows,
          hide: ![RowTypes.RowGroup, RowTypes.Activity, RowTypes.Grid].includes(this.row.type),
          testId: "remove-row",
        },
        {
          icon: icons.ArrowRight,
          title: this.$t("contextMenu.activity.addArrow"),
          func: this.addArrow,
          hide: !(this.row.type === RowTypes.Activity) || this.current.type === ProcessTypes.Grid,
          disabled: this.design.arrows[this.row.id]?.length === 3,
          testId: "add-arrow",
        },
        {
          icon: icons.DeleteArrow,
          title: this.$t("contextMenu.activity.deleteArrow"),
          func: this.deleteArrow,
          hide:
            !(this.row.type === RowTypes.Activity || this.row.type === RowTypes.RowGroup) ||
            this.current.type === ProcessTypes.Grid,
          disabled:
            !this.$store.state.cells.selectedArrow || this.$store.state.cells.selectedArrow.rowId !== this.row.id,
          testId: "delete-arrow",
        },
        {
          icon: icons.RemoveColumn,
          title: this.$t("contextMenu.activity.removeCol"),
          func: this.removeColumn,
          hide: ![ColumnTypes.Player, ColumnTypes.ColumnGroup, ColumnTypes.Grid].includes(this.column.type),
          testId: "remove-column",
        },
        {
          icon: icons.ChevronLeft,
          title: this.$t("contextMenu.activity.moveLeft"),
          func: this.movePlayerLeft,
          hide: ![ColumnTypes.Player, ColumnTypes.ColumnGroup, ColumnTypes.Grid].includes(this.column.type),
          testId: "move-left",
        },
        {
          icon: icons.ChevronRight,
          title: this.$t("contextMenu.activity.moveRight"),
          func: this.movePlayerRight,
          hide: ![ColumnTypes.Player, ColumnTypes.ColumnGroup, ColumnTypes.Grid].includes(this.column.type),
          testId: "move-right",
        },
        {
          icon: icons.ChevronUp,
          title: this.$t("contextMenu.activity.moveUp"),
          func: this.moveRowUp,
          hide: ![RowTypes.RowGroup, RowTypes.Activity, RowTypes.Grid].includes(this.row.type),
          testId: "move-up",
        },
        {
          icon: icons.ChevronDown,
          title: this.$t("contextMenu.activity.moveDown"),
          func: this.moveRowDown,
          hide: ![RowTypes.RowGroup, RowTypes.Activity, RowTypes.Grid].includes(this.row.type),
          testId: "move-down",
        },
      ];
    },
    showOptions() {
      return this.data.filter((item) => !item.hide);
    },
    column() {
      return this.columns[this.mainRange?.range[0]];
    },
    row() {
      return this.rows[this.mainRange?.range[1]];
    },
    mode() {
      // horizontal vertical
      const boundaries = [0, 0, this.columns.length - 1, this.rows.length - 1];
      return this.mainRange.range[1] === boundaries[1] && this.mainRange.range[3] === boundaries[3]
        ? "kk-designer__horizontal"
        : "kk-designer__vertical";
    },
    position() {
      return this.calculatedPosition;
    },
  },
  watch: {
    mode: "updatePosition",
    row: "updatePosition",
    column: "updatePosition",
  },
  mounted() {
    this.updatePosition();
  },
  methods: {
    updatePosition() {
      this.$nextTick(() => {
        const id = `${this.row.id}_${this.column.id}`;
        const element =
          this.mode !== "kk-designer__horizontal"
            ? document.querySelector(`.kk-designer__rowletter-active`)
            : document.querySelector(`.kk-designer__columnletter-active`);

        if (!element) {
          this.calculatedPosition = {};
          return;
        }

        const boundElement = element.getBoundingClientRect();
        const leftOffset = this.mode === "kk-designer__horizontal" ? 10 : 50;
        const topOffset = this.mode === "kk-designer__horizontal" ? 90 : 0;

        if (boundElement) {
          const { x, y, width, height } = boundElement;
          this.calculatedPosition = {
            left: `${x - leftOffset}px`,
            top: `${y - topOffset}px`,
            width: `${width}px`,
            height: `${height}px`,
          };
        }
      });
      this.setTransform();
    },
    setTransform() {
      const element =
        this.mode !== "kk-designer__horizontal"
          ? document.querySelector(`.kk-designer__rowletter-active`)
          : document.querySelector(`.kk-designer__columnletter-active`);
      const menuElement = document.querySelector(`.kk-designer__activity-menu`);
      const boundElement = menuElement.getBoundingClientRect();

      let translateX = "0";
      translateX = element?.getBoundingClientRect()?.right > window.innerWidth ? "-100%" : translateX;
      const translateY =
        element?.getBoundingClientRect().bottom + boundElement.height > window.innerHeight ? `-100%` : "-50%";
      this.transform = `translate(${translateX},${translateY})`;
    },
    addActivity() {
      this.$store.dispatch("cells/addRow", {
        type: RowTypes.Activity,
      });
    },
    addRow(type) {
      const range = this.$store.getters["cells/activeRange"]().range;
      const rowIds = this.$store.getters["cells/rowsInRange"](range);
      this.$store.dispatch("cells/addRow", { index: range[1], count: rowIds.length, type });
    },
    addColumn(type) {
      const range = this.$store.getters["cells/activeRange"]().range;
      const columnIds = this.$store.getters["cells/columnsInRange"](range);
      const payload = { index: range[0], count: columnIds.length, type };
      this.$store.dispatch("cells/addColumn", payload);
    },
    addArrow() {
      if (this.design.arrows[this.row.id]?.length === 3)
        return this.showDialog(this.$t("processDesigner.arrow.maxCount"));

      this.$store.dispatch("cells/setArrow", { rowId: this.row.id });
    },
    removeRows() {
      this.$store.dispatch("cells/removeRows");
    },
    removeColumn() {
      const range = this.$store.getters["cells/activeRange"]().range;
      const columnIds = this.$store.getters["cells/columnsInRange"](range);
      this.$store.dispatch("cells/removeColumns", columnIds);
    },
    movePlayerRight() {
      hotkeys.trigger("ctrl+right", HotkeyScopes.ProcessDesigner);
    },
    movePlayerLeft() {
      hotkeys.trigger("ctrl+left", HotkeyScopes.ProcessDesigner);
    },
    moveRowUp() {
      hotkeys.trigger("ctrl+up", HotkeyScopes.ProcessDesigner);
    },
    moveRowDown() {
      hotkeys.trigger("ctrl+down", HotkeyScopes.ProcessDesigner);
    },
    deleteArrow() {
      if (!this.$store.state.cells.selectedArrow)
        return this.showDialog(this.$t("processDesigner.arrow.noSelectedArrow"));

      if (this.$store.state.cells.selectedArrow?.rowId !== this.row.id)
        return this.showDialog(this.$t("processDesigner.arrow.notSameArrow"));

      this.$store.dispatch("cells/deleteSelectedArrow");
    },
    showDialog(summary) {
      const message = { severity: "error", life: 3000, summary };
      this.$store.dispatch("addToast", message);
    },
  },
};
</script>

<style lang="less" scoped>
@import "../../../../../assets/styles/variables";

.kk-designer {
  &__activity-menu {
    position: absolute;
    z-index: 210;
    border-radius: 5px;
    :deep(&-icon) {
      padding: 4px;
      color: @kk-color-primary-blue;
      background: @kk-color-gold;
      border-radius: 50%;
    }
    :deep(&-disabled) {
      cursor: no-drop;
    }
    :deep(&-disabled &-icon) {
      background: @kk-color-light-gray;
      color: black;
    }

    &-wrapper {
      display: flex;
      flex-direction: column;
      background: @kk-color-lighter-gray;
      box-shadow: rgba(0, 0, 0, 0.35) 0px 5px 15px;
      padding: 6px;
      border-radius: 30px;
      overflow: hidden;
      gap: 6px;
    }
    &_container {
      position: absolute;
      display: flex;
      justify-content: center;
      align-items: center;
    }
  }
  &__vertical {
    flex-direction: column;
  }
  &__horizontal {
    flex-direction: row;
    .kk-designer__activity-menu-icon {
      background-color: @kk-color-light-blue;
    }
  }
}
</style>
